﻿import { format, parseISO, differenceInMinutes } from 'date-fns';
import { CancelReason, ParkingSessionStatus } from '@/graphql/generated';
import type { ParkingSessionDisplayStatus } from './types';

export const formatDateTime = (
	dateString?: string | null
): string | undefined => {
	if (!dateString) {
		return undefined;
	}
	try {
		return format(parseISO(dateString), 'MMM dd, yyyy HH:mm');
	} catch {
		return undefined;
	}
};

export const formatDate = (dateString?: string | null): string | undefined => {
	if (!dateString) {
		return undefined;
	}
	try {
		return format(parseISO(dateString), 'MMM dd, yyyy');
	} catch {
		return undefined;
	}
};

export const formatTime = (dateString?: string | null): string | undefined => {
	if (!dateString) {
		return undefined;
	}
	try {
		return format(parseISO(dateString), 'HH:mm');
	} catch {
		return undefined;
	}
};

export const formatCurrency = (
	amount?: number | null
): string | undefined => {
	if (amount === null || amount === undefined) {
		return undefined;
	}
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'EUR',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2
	}).format(amount);
};

export const formatDuration = (
	minutes?: number | null
): string | undefined => {
	if (!minutes) {
		return undefined;
	}

	const minutesInDay = 24 * 60;
	const days = Math.floor(minutes / minutesInDay);
	const remainingAfterDays = minutes % minutesInDay;
	const hours = Math.floor(remainingAfterDays / 60);
	const mins = remainingAfterDays % 60;

	const parts: string[] = [];
	if (days > 0) {
		parts.push(`${days}d`);
	}
	if (hours > 0) {
		parts.push(`${hours}h`);
	}
	if (mins > 0 || parts.length === 0) {
		parts.push(`${mins}m`);
	}

	return parts.join(' ');
};

export const calculateDuration = (
	startDateTime?: string | null,
	endDateTime?: string | null
): number | null => {
	if (!startDateTime) {
		return null;
	}

	try {
		const start = parseISO(startDateTime);
		const end = endDateTime ? parseISO(endDateTime) : new Date();
		return differenceInMinutes(end, start);
	} catch {
		return null;
	}
};

export const getParkingSessionDisplayStatus = (
	status?: ParkingSessionStatus | null
): ParkingSessionDisplayStatus => {
	switch (status) {
		case ParkingSessionStatus.Inside:
			return 'Inside';
		case ParkingSessionStatus.Exited:
			return 'Exited';
		case ParkingSessionStatus.Canceled:
			return 'Canceled';
		default:
			return 'Unknown';
	}
};

export const getStatusColor = (
	status?: ParkingSessionDisplayStatus
): string => {
	switch (status) {
		case 'Inside':
			return 'text-blue-600 dark:text-blue-400';
		case 'Exited':
			return 'text-gray-600 dark:text-gray-400';
		case 'Canceled':
			return 'text-red-600 dark:text-red-400';
		default:
			return 'text-gray-600 dark:text-gray-400';
	}
};

export const getStatusBadgeVariant = (
	status?: ParkingSessionDisplayStatus
): 'default' | 'secondary' | 'outline' | 'destructive' => {
	switch (status) {
		case 'Inside':
			return 'default';
		case 'Exited':
			return 'outline';
		case 'Canceled':
			return 'destructive';
		default:
			return 'secondary';
	}
};

export const formatVehicleDescription = (
	make?: string,
	model?: string,
	color?: string
): string => {
	const parts = [];

	if (make && model) {
		parts.push(`${make} ${model}`);
	} else if (make) {
		parts.push(make);
	} else if (model) {
		parts.push(model);
	}

	if (color) {
		parts.push(color);
	}

	return parts.length > 0 ? parts.join(' · ') : 'Vehicle details unavailable';
};

export const formatCustomerName = (
	isCompany?: boolean | null,
	firmName?: string | null,
	firstName?: string | null,
	lastName?: string | null
): string | undefined => {
	if (isCompany && firmName) {
		return firmName;
	}

	if (firstName && lastName) {
		return `${firstName} ${lastName}`;
	}

	if (firstName) {
		return firstName;
	}

	if (lastName) {
		return lastName;
	}

	if (firmName) {
		return firmName;
	}

	return undefined;
};

export const getCancelReasonKey = (
	reason?: CancelReason | null
): string => {
	switch (reason) {
		case CancelReason.VehicleBackedUp:
			return 'cancelReasons.vehicleBackedUp';
		case CancelReason.DuplicateEntry:
			return 'cancelReasons.duplicateEntry';
		case CancelReason.Unknown:
			return 'cancelReasons.unknown';
		default:
			return 'cancelReasons.unknown';
	}
};
